import { Autocomplete, AutocompleteChangeDetails, Button, Grid, TextField } from '@mui/material';
import CustomAccordion from '../CustomAccordion/CustomAccordion';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import { BrokerAccountInputSelector, resetBrokerAccountInput, setAccountType, setBrokerAccountInput, setBrokerAccountInputExpand, setBrokerId, setContactId, setContactName, setExpiredDate, setMicName, setRemark, setSelectedBrokerAccountId, setStartedDate } from './reducers/BrokerAccountInputReducer';
import BrokerAccountApi from './api/BrokerAccountApi';
import BrokerAccountModel from './models/BrokerAccountModel';
import { BrokerAccountInputModel } from './models/BrokerAccountInputModel';
import ContactNameModel from '../ContactSelector/models/ContactNameModel';
import { contactSelectorSelector, setContactSearchResults } from '../ContactSelector/reducers/ContactSelectorReducer';
import { contactInputSelector } from '../Contact/reducers/ContactInputReducer';
import UserSelector from '../UserSelector/UserSelector';
import StartEndDatepicker from '../StartEndDatepicker/StartEndDatepicker';
import CompanyNamePICModel from '../CompanyNamePIC/models/CompanyNamePICModel';
import CompanyNamePICSelector from '../CompanyNamePICSelector/CompanyNamePICSelector';
import ContactSelector from '../ContactSelector/ContactSelector';

const BrokerAccountInput: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const brokerAccountInputViewData = useSelector(BrokerAccountInputSelector);
    const contactSelectorData = useSelector(contactSelectorSelector);
    const contactInputViewData = useSelector(contactInputSelector);

    useEffect(() => {
        if (brokerAccountInputViewData.triggerSave > 0) {
            save();
        }
    }, [brokerAccountInputViewData.triggerSave]);

    useEffect(() => {
        if (brokerAccountInputViewData.triggerRefreshInput > 0) {
            refreshInput();
        }
    }, [brokerAccountInputViewData.triggerRefreshInput]);

    const refreshInput = () => {
        dispatch(resetBrokerAccountInput());
        dispatch(setSelectedBrokerAccountId(""));
    }

    useEffect(() => {
        if (brokerAccountInputViewData.selectedBrokerAccountId.length > 0) {
            selectBrokerAccountById(brokerAccountInputViewData.selectedBrokerAccountId);
        }
    }, [brokerAccountInputViewData.selectedBrokerAccountId])

    const selectBrokerAccountById = (id: string) => {
        dispatch(showLoading(true));
        BrokerAccountApi.GetById({
            id: id
        }).then((response: BaseResponse<BrokerAccountModel>) => {
            if (response.success) {
                let brokerAccountInput = Utility.CloneObject(brokerAccountInputViewData.BrokerAccountInput) as BrokerAccountInputModel;

                brokerAccountInput.id = response.data.id;
                brokerAccountInput.contactId = response.data.contactId;
                brokerAccountInput.micName = response.data.micName;
                brokerAccountInput.accountType = response.data.accountType;
                brokerAccountInput.brokerId = response.data.brokerId;
                brokerAccountInput.startedDate = Utility.DateFormat.formatDateString(response.data.startedDate).toUpperCase();
                brokerAccountInput.expiredDate = Utility.DateFormat.formatDateString(response.data.expiredDate).toUpperCase();
                brokerAccountInput.remark = response.data.remark;

                setContactForContactSelector({
                    id: response.data.contactId,
                    companyName: response.data.companyName
                } as ContactNameModel);

                dispatch(setBrokerAccountInput(brokerAccountInput));

                dispatch(showLoading(false));

            } else {
                Utility.Alert.Error(response.message);
                dispatch(showLoading(false));
            }
        }).catch((error) => {
            Utility.Alert.Error(error);
            dispatch(showLoading(false));
        })
    };

    const save = () => {
        try {
            validate();

            BrokerAccountApi.Save(brokerAccountInputViewData.BrokerAccountInput)
                .then((response: BaseResponse<BrokerAccountModel>) => {
                    if (response.success) {
                        if (brokerAccountInputViewData.BrokerAccountInput.id.length === 0) {
                            Utility.Alert.Success("BrokerAccount added successful");
                        } else {
                            Utility.Alert.Success("BrokerAccount updated successful");
                        }

                        refreshInput();
                    } else {
                        Utility.Alert.Error(response.message);
                    }
                })
                .catch((error) => {
                    Utility.Alert.Error(error);
                });
        } catch (error) {
            Utility.Alert.Error(error);
        }
    }

    const validate = () => {
        if (brokerAccountInputViewData.BrokerAccountInput.contactId.length === 0) {
            throw "Contact Name is required";
        }

        if (brokerAccountInputViewData.BrokerAccountInput.accountType.length === 0) {
            throw "Account Type is required";
        }

        if (brokerAccountInputViewData.BrokerAccountInput.brokerId === 0) {
            throw "Broker is required";
        }

        if (brokerAccountInputViewData.BrokerAccountInput.accountType === "OA" ||
            brokerAccountInputViewData.BrokerAccountInput.accountType === "NA" || 
            brokerAccountInputViewData.BrokerAccountInput.accountType === "FA"
         ) {
            if (brokerAccountInputViewData.BrokerAccountInput.expiredDate.length === 0) {
                throw "Expired date is required";
            }
        }
    }

    const setContactForContactSelector = (data: ContactNameModel) => {
        let searchResults = Utility.CloneObject(contactSelectorData.searchResults) as ContactNameModel[];

        searchResults.push(data);

        searchResults = _.uniqBy(searchResults, (item: ContactNameModel) => {
            return item.id;
        });
        dispatch(setContactSearchResults(searchResults));
    }

    return (
        <>
            {
                contactInputViewData.staticResources.companyTypes.length > 0 &&
                <CustomAccordion
                    title="Input"
                    defaultOpen={true}
                    className="top-quick-input"
                    open={brokerAccountInputViewData.BrokerAccountInputExpand}
                    onOpenChange={(isOpen: boolean) => {
                        dispatch(setBrokerAccountInputExpand(isOpen));
                    }}
                >
                    <Grid container>
                        <Grid item lg={3} xs={12}>
                            <Grid container className='cargo-input-component'>
                                <Grid xs={10} lg={9}>
                                    <ContactSelector
                                        label='Company Name'
                                        className="cargo-input-component"
                                        selectedContactId={brokerAccountInputViewData.BrokerAccountInput.contactId ? brokerAccountInputViewData.BrokerAccountInput.contactId : ""}
                                        currentContactName={brokerAccountInputViewData.BrokerAccountInput.contactName ? brokerAccountInputViewData.BrokerAccountInput.contactName : ""}
                                        useFreeText={true}
                                        triggerInitContactName={brokerAccountInputViewData.triggerFocusCompanyNameInput}
                                        onContactIdChange={(value: string) => {
                                            dispatch(setContactId(value));
                                        }}
                                        onContactNameChange={(value: string) => {
                                            dispatch(setContactName(value));
                                        }}
                                    >
                                    </ContactSelector>
                                </Grid>
                                <Grid item xs={2} lg={3}>
                                    <Button
                                        className="clear-input-button"
                                        tabIndex={-1}
                                        color="success"
                                        variant="contained"
                                        onClick={() => {
                                            refreshInput();
                                        }}
                                    >
                                        <i className="fa fa-refresh" aria-hidden="true"></i>
                                    </Button>
                                </Grid>
                            </Grid>
                            <Grid container className='cargo-input-component'>
                                <Grid item xs={12} lg={12}>
                                    <TextField
                                        className='cargo-input-component'
                                        fullWidth
                                        label="Mic Name"
                                        value={brokerAccountInputViewData.BrokerAccountInput.micName}
                                        onChange={(event) => {
                                            dispatch(setMicName(event.target.value));
                                        }}
                                    >
                                    </TextField>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item lg={2} xs={12}>
                            <Autocomplete
                                className='cargo-input-component'
                                options={contactInputViewData.staticResources.accountTypes}
                                renderInput={(params) => <TextField {...params} label="Account Type" />}
                                value={brokerAccountInputViewData.BrokerAccountInput.accountType ? brokerAccountInputViewData.BrokerAccountInput.accountType : ""}
                                onChange={(event: React.SyntheticEvent, value: string | null, reason: string, details?: AutocompleteChangeDetails<string> | undefined) => {
                                    dispatch(setAccountType(value ? value : ""));
                                }}
                            />
                            <UserSelector
                                label='Owner Broker'
                                className='cargo-input-component'
                                selectedUserId={brokerAccountInputViewData.BrokerAccountInput.brokerId}
                                onUserIdChange={(value: number) => {
                                    dispatch(setBrokerId(value));
                                }}
                            >
                            </UserSelector>
                        </Grid>
                        <Grid item lg={2} xs={12}>
                            <StartEndDatepicker
                                startDateLabel="Started Date"
                                useQuickEndDateInput={false}
                                className='cargo-input-component'
                                onStartDateChange={(date: string) => {
                                    if (date) {
                                        let expireDate = "";
                                        if (brokerAccountInputViewData.BrokerAccountInput.accountType == "NA") {
                                            expireDate = Utility.DateFormat.format(Utility.addDaysToDate(new Date(date), 365));
                                        } else if (brokerAccountInputViewData.BrokerAccountInput.accountType == "OA") {
                                            expireDate = Utility.DateFormat.format(Utility.addDaysToDate(new Date(date), 180));
                                        } else if (brokerAccountInputViewData.BrokerAccountInput.accountType == "FA") {
                                            expireDate = Utility.DateFormat.format(Utility.addDaysToDate(new Date(date), 180));
                                        }

                                        dispatch(setExpiredDate(expireDate));

                                        dispatch(setStartedDate(date));
                                    }
                                }}
                                startDateValue={brokerAccountInputViewData.BrokerAccountInput.startedDate.toUpperCase()}
                                useSingleMode={true}
                            >
                            </StartEndDatepicker>
                            <StartEndDatepicker
                                startDateLabel="Expired Date"
                                useQuickEndDateInput={false}
                                className='cargo-input-component'
                                onStartDateChange={(date: string) => {
                                    if (date) {
                                        dispatch(setExpiredDate(date));
                                    }
                                }}
                                startDateValue={brokerAccountInputViewData.BrokerAccountInput.expiredDate.toUpperCase()}
                                useSingleMode={true}
                            >
                            </StartEndDatepicker>
                        </Grid>
                        <Grid item lg={4} xs={12}>
                            <TextField
                                label="Remarks"
                                multiline
                                fullWidth
                                rows={3}
                                value={brokerAccountInputViewData.BrokerAccountInput.remark}
                                onChange={(event) => {
                                    dispatch(setRemark(event.target.value ? event.target.value : ""));
                                }}
                                className='cargo-input-component tonnage-input-full-tc'
                            />
                        </Grid>
                        <Grid item lg={1} xs={12}>
                            <Button
                                onClick={save}
                                variant="outlined"
                            >
                                Save
                            </Button>
                        </Grid>
                    </Grid>
                </CustomAccordion>
            }
        </>
    )
};

export default BrokerAccountInput;